import { cssColorToFlutter } from '@/shared/lib/flutter-color';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { EMPTY_GUID } from '@/shared/lib/empty-guid';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import type {
  AppointmentResponse,
  DeviceAppointmentProfile,
  DeviceUpdateRequest,
} from '@/types/scheduling-api';
import type { EditRecurringPayload } from '@/scheduling/components/appointments/edit-recurring-scope-dialog';

/**
 * The EDIT body for a DEVICE booking — `PUT /api/Appointment/Resource/{id}`.
 *
 * PROD-2526. This file used to build the PROVIDER edit body (`PUT
 * /api/Appointment/{id}`, an echo of the loaded PreLoad with the editable
 * fields overlaid), because the device edit rail saved through the provider
 * route. That route answered 500 for every call on dev, and since PROD-2526 it
 * refuses a device appointment outright (`DeviceUpdateOnProviderRoute`,
 * BR-SCHED-037). The device has had its own edit route since PROD-2385, with
 * series edits since PROD-2383, and this builds ITS body instead:
 * `UpdateResourceAppointmentRequest`, which has no participant array, no
 * reminder profile and no specialty — the device and the patient travel as
 * plain ids the server checks against the stored booking.
 *
 * What did NOT change is the money rule this file exists to protect.
 *
 * THE ONE THING THIS FILE EXISTS TO PROTECT
 * -----------------------------------------
 * A device booking is priced by its product's own service (PROD-2165). Billing's
 * `RemoveAddedServices` hard-deletes every individually-added line that is not in
 * the `addedServices` the request carries, so an edit that forgets that id
 * silently deletes the invoice line. Scheduling added
 * `CarryDeviceProductServiceAsync` to re-derive it server-side (the device route
 * runs the same engine, so it runs there too), but that is explicitly
 * BEST-EFFORT (a failed or empty read logs a warning and sends exactly what the
 * caller sent) and it reads LIVE profiles only — PROD-2170: a product that has
 * since been archived is not found and the line still drops.
 *
 * So this builder ships the chosen product's `serviceId` itself, in addition to
 * the invoice's existing lines. The server unions rather than overwrites
 * (`model.AddedServices.Union(new[]{ serviceId })`, with `Distinct()` behind it in
 * Billing's `GetIndividualServices`), so sending it cannot double-count — it can
 * only stop the line disappearing when the server-side re-read misses.
 *
 * This is a DELIBERATE divergence from `device-booking-request.ts`, which does not
 * send it. On CREATE the id is precedence (`ResolveProductServiceId` /
 * `ResolveBillingProfileId` treat a sent value as "requested wins"), so echoing a
 * cached snapshot could pin a stale price. On EDIT there is no resolution step:
 * `addedServices` is the surviving-lines list and nothing else, and omitting an id
 * is the destructive option.
 */

/** Empty codings — see `coalesceReasonCode` / `coalesceAppointmentType`. */
const EMPTY_REASON_CODE = { code: '', display: '', definition: '', system: null };
const ROUTINE_APPOINTMENT_TYPE = {
  code: 'ROUTINE',
  display: 'Routine',
  definition: null,
  system: null,
};

type Loose = Record<string, unknown>;

interface ParticipantLike {
  referenceType?: string | null;
  reference?: string | null;
  display?: string | null;
  provider?: unknown;
}

/** The `Device` participant, which is what makes this a device booking. */
export function findDeviceParticipant<T extends ParticipantLike>(
  participants: readonly T[] | undefined | null,
): T | undefined {
  return participants?.find(
    (p) => (p.referenceType ?? '').toLowerCase() === 'device',
  );
}

function hasProviderParticipant(appt: AppointmentResponse): boolean {
  return Boolean(
    (appt.participant as ParticipantLike[] | undefined)?.some((p) => {
      const rt = (p.referenceType ?? '').toLowerCase();
      return rt === 'provider' || rt === 'practitioner';
    }),
  );
}

/**
 * Is this appointment a device booking?
 *
 * Both halves are required, and in this order. "Has a Device participant" alone
 * would misroute a hypothetical appointment that has BOTH (a practitioner using a
 * machine) into the device rail, which offers no provider control — that would be
 * a regression of the revenue-carrying path. A device booking is defined by the
 * ABSENCE of a provider, which is exactly how the server reads it
 * (`providerParticipant?.Reference` → null → the device branches).
 */
export function isDeviceAppointment(
  appt: AppointmentResponse | null | undefined,
): boolean {
  if (!appt) return false;
  return !hasProviderParticipant(appt) && !!findDeviceParticipant(appt.participant);
}

/** The device this booking is on. */
export function deviceIdOf(appt: AppointmentResponse): string | null {
  const ref = findDeviceParticipant(appt.participant)?.reference;
  return ref && ref !== EMPTY_GUID ? ref : null;
}

/** The device's display name when the wire carried one. */
export function deviceNameOf(appt: AppointmentResponse): string | null {
  const p = findDeviceParticipant(appt.participant) as ParticipantLike | undefined;
  return p?.display?.trim() || null;
}

/**
 * The device PRODUCT this booking currently carries.
 *
 * The appointment points at a profile SNAPSHOT (a row in Scheduling's own
 * `AppointmentProfile`), and that snapshot's `ReferenceId` is the device
 * product's id, stamped at create (`ResolveDeviceBookingAsync` →
 * `CreateAppointmentProfile`). `referenceId` is therefore the id to match against
 * `GET /api/Resource/{id}/Schedule/AppointmentProfiles`, not `id`.
 */
export function currentDeviceProductId(appt: AppointmentResponse): string | null {
  const profile = appt.appointmentProfile as unknown as Loose | undefined;
  const ref = profile?.referenceId as string | undefined;
  if (ref && ref !== EMPTY_GUID) return ref;
  const id = profile?.id as string | undefined;
  return id && id !== EMPTY_GUID ? id : null;
}

/**
 * The invoice's individually-added service lines, from wherever the wire put
 * them. Same two sources (and same precedence) the practitioner overlay reads.
 */
export function loadedServiceLines(appt: AppointmentResponse): Loose[] {
  const la = appt as unknown as Loose;
  const echoed = Array.isArray(la.addedIndividualServices)
    ? (la.addedIndividualServices as Loose[])
    : [];
  if (echoed.length) return echoed;
  const invoice = la.invoice as { services?: unknown } | undefined;
  return Array.isArray(invoice?.services) ? (invoice!.services as Loose[]) : [];
}

function lineServiceId(line: Loose): string | null {
  const id = line.serviceId;
  return typeof id === 'string' && id.length > 0 && id !== EMPTY_GUID ? id : null;
}

export interface DeviceEditDraft {
  /** The product the rail has selected (unchanged or newly picked). */
  product: DeviceAppointmentProfile;
  /** The local instant the booking starts. `end` is derived from the product. */
  start: Date;
  /**
   * EVERY service on the booking that is not the product's own — i.e. exactly
   * the removable chips the rail is showing. This list is AUTHORITATIVE, not a
   * delta: what the operator can see is what gets saved, and a service they
   * removed does not come back through a hidden echo. Seed it with
   * `seedDeviceEditServices`.
   */
  extraServiceIds: readonly string[];
  /** Internal note. */
  comment: string | null;
}

/**
 * The rail's initial service chips: everything the invoice already bills, minus
 * the current product's own service (which the rail shows separately as an
 * unremovable "included" row, because removing it could not change the invoice).
 *
 * `currentProduct` is null when the device's live product list has not resolved
 * yet, or when the booking's product has since been ARCHIVED. In that case its
 * service stays in the list as an ordinary removable chip rather than vanishing
 * — the operator can see it and decide, and no line is ever deleted on a guess.
 */
export function seedDeviceEditServices(
  appt: AppointmentResponse,
  currentProduct: DeviceAppointmentProfile | null | undefined,
): string[] {
  const productServiceId = currentProduct?.serviceId ?? null;
  const out: string[] = [];
  for (const line of loadedServiceLines(appt)) {
    const id = lineServiceId(line);
    if (id && id !== productServiceId && !out.includes(id)) out.push(id);
  }
  return out;
}

function coalesceReasonCode(profile: Loose, root: Loose): unknown {
  return profile.reasonCode ?? root.reasonCode ?? { ...EMPTY_REASON_CODE };
}

function coalesceAppointmentType(
  profile: Loose,
  root: Loose,
  product: DeviceAppointmentProfile,
): unknown {
  const fromProduct = product.appointmentTypeId?.trim();
  return (
    profile.appointmentType ??
    root.appointmentType ??
    (fromProduct
      ? { code: fromProduct, display: fromProduct, definition: null, system: null }
      : { ...ROUTINE_APPOINTMENT_TYPE })
  );
}

/**
 * The service set the PUT must carry, and the line objects that mirror it.
 *
 *   1. the CHOSEN product's own service goes first — it is the base price, and
 *      shipping it is what survives a best-effort server-side miss (PROD-2170:
 *      `GetResourceAppointmentProfiles` asks for live profiles only, so an
 *      archived product is not found and the line would drop). The server unions
 *      rather than overwrites, so sending it cannot double-count;
 *   2. then the rail's visible chips, in their order.
 *
 * Nothing is pulled silently out of the loaded invoice: the draft is the whole
 * list (`seedDeviceEditServices` puts the invoice's existing lines there at
 * open), so a service the operator removed stays removed. The loaded lines are
 * still READ, but only to reuse each line's own id / procedure code rather than
 * re-synthesising it.
 */
export function deviceEditServiceSet(
  appt: AppointmentResponse,
  draft: DeviceEditDraft,
): { addedServices: string[]; addedIndividualServices: Loose[] } {
  const byId = new Map<string, Loose>();
  for (const line of loadedServiceLines(appt)) {
    const id = lineServiceId(line);
    if (id) byId.set(id, line);
  }

  const ids = new Set<string>();
  // ASYMMETRY WITH CREATE, AND IT IS DELIBERATE. Do not "normalise" the two
  // builders without reading both comments.
  //   EDIT (here): SEND the product's own serviceId. The server re-reads the
  //     product to re-derive the price (CarryDeviceProductServiceAsync), and
  //     when that re-read misses — an ARCHIVED product, PROD-2170, still open
  //     upstream — Billing's RemoveAddedServices deletes the priced line. The
  //     server unions and de-dupes (AddedServices.Union(...) + Distinct()), so
  //     sending it cannot double-count; omitting it can silently lose money.
  //   CREATE (`device-booking-request.ts`): do NOT send it. There the id is
  //     PRECEDENCE, and echoing a cached snapshot would pin a price the
  //     practice may since have changed.
  if (draft.product.serviceId) ids.add(draft.product.serviceId);
  for (const id of draft.extraServiceIds) {
    if (id && id !== EMPTY_GUID) ids.add(id);
  }

  const addedServices = [...ids];
  const addedIndividualServices = addedServices.map((serviceId) => {
    const line = byId.get(serviceId);
    return {
      id: line?.id ?? null,
      procedureCode: line?.procedureCode ?? null,
      isIndividualAdded: true,
      serviceId,
      shortDescription: line?.shortDescription ?? null,
    };
  });

  return { addedServices, addedIndividualServices };
}

/** The patient this booking is for. */
export function patientIdOf(appt: AppointmentResponse): string | null {
  const p = findPatientParticipant(appt.participant) as
    | { patient?: { id?: string | null } | null; reference?: string | null }
    | undefined;
  const id = p?.patient?.id ?? p?.reference ?? null;
  return id && id !== EMPTY_GUID ? id : null;
}

/**
 * A SERIES edit (PROD-2383 on the device route): which sessions of the course
 * the edit reaches. Omitted, the edit changes this session alone — the same
 * wire meaning as `editRecurring: null` on the provider route.
 */
export interface DeviceSeriesEdit {
  /** `buildEditRecurringPayload`'s answer; null (this-only) edits this session alone. */
  editRecurring: EditRecurringPayload | null;
}

/**
 * Build the device EDIT body for `PUT /api/Appointment/Resource/{id}`.
 *
 * Editable, and nothing else: product (which carries duration), time, services,
 * note — the same four the device edit rail always offered (ruling
 * 2026-09-14). The device and the patient are FIXED: they are sent as the ids
 * the booking already has, and the server refuses a different one
 * (`DeviceImmutable` / `PatientImmutable`).
 *
 * A series edit (`series`) carries the course the session belongs to and the
 * scope; the server moves every reached session to the new TIME on its own
 * day, exactly as the provider route does (`RecurringAppointmentOrchestrator`).
 */
export function buildDeviceUpdateRequest(
  loadedAppt: AppointmentResponse,
  draft: DeviceEditDraft,
  userId: string,
  series?: DeviceSeriesEdit | null,
): DeviceUpdateRequest {
  const la = loadedAppt as unknown as Loose;
  const loadedProfile = (la.appointmentProfile ?? {}) as Loose;
  const currentProductId = currentDeviceProductId(loadedAppt);
  const productChanged = draft.product.id !== currentProductId;

  const minutesDuration = draft.product.minutesDuration;
  const end = new Date(draft.start.getTime() + minutesDuration * 60_000);

  // The server dereferences both codings UNCONDITIONALLY on the profile it
  // builds (`existingModel.ReasonCodeId = model.AppointmentProfile.ReasonCode.Code`,
  // `model.AppointmentType.Code` for the slot move). A device booking is created
  // with NO reason code, and PreLoad can resolve that empty id to null — so the
  // empty coding is sent, which lands the server on the value it already stores.
  const reasonCode = coalesceReasonCode(loadedProfile, la) as DeviceUpdateRequest['reasonCode'];
  const appointmentType = coalesceAppointmentType(
    loadedProfile,
    la,
    draft.product,
  ) as DeviceUpdateRequest['appointmentType'];

  // Unchanged product → the profile SNAPSHOT id, so the server updates that row
  // and never touches its ReferenceId (the pointer CarryDeviceProductServiceAsync
  // re-reads the product from). Changed product → the PRODUCT id, which the
  // server looks up in its own table, misses, and therefore CREATES a fresh
  // snapshot whose ReferenceId IS that product (`CreateProfile`:
  // `ReferenceId = model.AppointmentProfile.Id`).
  const appointmentProfileId = productChanged
    ? draft.product.id
    : ((loadedProfile.id as string | undefined) ?? currentProductId ?? draft.product.id);
  const profileName = productChanged
    ? draft.product.profileName
    : ((loadedProfile.profileName as string | undefined) ?? draft.product.profileName);
  // Flutter renders the calendar card with `Color(int.tryParse(color)!)`, which
  // throws on a CSS `#RRGGBB` and gray-boxes the appointment.
  const rawColor = productChanged ? draft.product.color : (loadedProfile.color as string | undefined);
  const color = cssColorToFlutter(rawColor ?? undefined) ?? (loadedProfile.color as string | undefined) ?? null;
  // Mirrors `ResolveDeviceBookingAsync`: a product priced by its own service
  // must NOT also be priced by a bundle, or the booking is charged twice.
  const billingProfileId = productChanged
    ? draft.product.serviceId
      ? null
      : (draft.product.billingProfileId ?? null)
    : ((loadedProfile.billingProfileId as string | null | undefined) ?? null);

  const { addedServices } = deviceEditServiceSet(loadedAppt, draft);

  const invoiceId = (loadedAppt.invoice as { id?: string | null } | undefined)?.id ?? EMPTY_GUID;
  const policyNo =
    (la.policyNo as string | null | undefined) ??
    ((la.invoice as { policyNo?: string } | undefined)?.policyNo ?? null);
  const residentialAddressId =
    (la.residentialAddressId as string | null | undefined) ??
    ((la.residentialAddress as { id?: string } | undefined)?.id ?? null);
  const callBackContractId = (la.callBackContractId as string | null | undefined) || null;

  return {
    id: loadedAppt.id,
    deviceId: deviceIdOf(loadedAppt) ?? EMPTY_GUID,
    patientId: patientIdOf(loadedAppt) ?? EMPTY_GUID,
    appointmentProfileId,
    invoiceId,
    profileName,
    color,
    billingProfileId,
    location: loadedAppt.location,
    organizationId: loadedAppt.organizationId,
    practiceId: loadedAppt.practiceId,
    roomId: loadedAppt.roomId || EMPTY_GUID,
    appointmentType,
    reasonCode,
    status: loadedAppt.status,
    priority: loadedAppt.priority ?? 0,

    // ── The editable four ─────────────────────────────────────────────────
    start: formatLocalDatetimeForWire(draft.start),
    end: formatLocalDatetimeForWire(end),
    minutesDuration,
    comment: draft.comment ?? null,
    addedServices,

    // ── Carried as booked ─────────────────────────────────────────────────
    description: loadedAppt.description ?? null,
    patientInstruction: loadedAppt.patientInstruction ?? null,
    flagList: loadedAppt.flagList ?? [],
    updatedBy: userId,
    sponsorType: loadedAppt.sponsorType ?? null,
    sponsor: loadedAppt.sponsor ?? null,
    policyNo,
    billingTotal: loadedAppt.billingTotal ?? null,
    paymentLink: loadedAppt.paymentLink ?? null,
    residentialAddressId,
    callBackContractId,

    // ── A series edit, or none ────────────────────────────────────────────
    // Both or neither: a Recurring with no scope would ask the server to re-read the
    // course for nothing, and a scope with no Recurring reaches no session.
    recurring: series?.editRecurring && loadedAppt.recurring ? loadedAppt.recurring : null,
    editRecurring: series?.editRecurring && loadedAppt.recurring ? series.editRecurring : null,
  };
}

// ── Edit-mode helpers for the Book-a-device rail (PROD-2526) ────────────────

/**
 * Statuses under which a device booking may still be edited — the same gate the
 * practitioner rail applies (`EDITABLE_EDIT_STATUSES`). A Cancelled / Fulfilled /
 * NoShow booking is history; editing it would re-price a closed invoice.
 * Carried over from the retired `edit-device-panel.tsx`.
 */
const EDITABLE_DEVICE_STATUSES = new Set(['booked', 'proposed', 'pending']);

export function isEditableDeviceBooking(appt: AppointmentResponse): boolean {
  return EDITABLE_DEVICE_STATUSES.has((appt.status?.code ?? '').toLowerCase());
}

/** The patient's display name, from the loaded booking. */
export function patientLabelOf(appt: AppointmentResponse): string {
  const p = findPatientParticipant(appt.participant)?.patient as
    | { firstName?: string; middleName?: string; lastName?: string; name?: string }
    | undefined;
  if (!p) return 'Patient';
  return (
    [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ').trim() || p.name || 'Patient'
  );
}

interface SlotLike {
  id: string;
  start: string;
  end: string;
}

/**
 * The server's free starts PLUS the booking's own current start, kept in order.
 *
 * The slot feed reports what is free, and this booking's own window is not —
 * this booking occupies it. Offering only the free list would make the booking's
 * current time unselectable, i.e. "you may edit the note, but only by moving the
 * appointment". The booking's own start is added back even when it is in the
 * PAST: correcting a past session's note or services is ordinary work, and a
 * rail that hid the time the session is actually at would imply it had moved.
 * The server re-checks the window under the device lock (PROD-1977), so the rail
 * is never stricter than it.
 */
export function withBookedStart<T extends SlotLike>(
  slots: readonly T[],
  booked: { start: string; end: string } | null,
  parse: (iso: string) => Date | null,
): (T | SlotLike)[] {
  if (!booked) return [...slots];
  const at = parse(booked.start)?.getTime();
  if (at == null) return [...slots];
  if (slots.some((s) => parse(s.start)?.getTime() === at)) return [...slots];
  const out: (T | SlotLike)[] = [...slots, { id: `booked-${booked.start}`, ...booked }];
  out.sort((a, b) => (parse(a.start)?.getTime() ?? 0) - (parse(b.start)?.getTime() ?? 0));
  return out;
}

/**
 * A TIME-ONLY move of a device booking (drag-and-drop, the series menu's
 * Reschedule) — the same device-route body the rail saves, with nothing but the
 * window changed. PROD-2526: device drag-and-drop used to call the provider's
 * `PUT /api/Appointment/Reschedule`; it now goes through the device route like
 * every other device edit.
 *
 * The product, its snapshot, the services and the note are carried exactly as
 * booked. The "product" handed to the builder is the booking's own snapshot, so
 * `productChanged` is false by construction and the snapshot id is echoed; the
 * duration is the dragged window's, which keeps the booking's own length.
 * Every invoice line is kept (`seedDeviceEditServices(appt, null)` subtracts
 * nothing), and the server's product carry unions the priced line back in, so a
 * move can never drop a price.
 */
export function buildDeviceMoveRequest(
  loadedAppt: AppointmentResponse,
  start: Date,
  end: Date,
  userId: string,
  series?: DeviceSeriesEdit | null,
): DeviceUpdateRequest {
  const profile = (loadedAppt.appointmentProfile ?? {}) as unknown as Loose;
  const minutes = Math.max(1, Math.round((end.getTime() - start.getTime()) / 60_000));
  const snapshot: DeviceAppointmentProfile = {
    id: currentDeviceProductId(loadedAppt) ?? EMPTY_GUID,
    resourceId: deviceIdOf(loadedAppt) ?? EMPTY_GUID,
    profileName: (profile.profileName as string | undefined) ?? '',
    minutesDuration: minutes,
    color: (profile.color as string | undefined) ?? null,
    billingProfileId: (profile.billingProfileId as string | undefined) ?? null,
    serviceId: null,
  } as DeviceAppointmentProfile;
  return buildDeviceUpdateRequest(
    loadedAppt,
    {
      product: snapshot,
      start,
      extraServiceIds: seedDeviceEditServices(loadedAppt, null),
      comment: loadedAppt.comment ?? null,
    },
    userId,
    series,
  );
}
